/**
 * skaile auth — manage CLI master-key strategy and secret provider status.
 */

import { type KeyStrategy, LocalSecretsProvider } from "@skaile/workspaces/secrets";
import { Command } from "commander";
import { logErr, logInfo, logOk } from "../helpers.ts";

const VALID_STRATEGIES: KeyStrategy[] = ["keychain", "passphrase", "machine"];

/**
 * Creates the `skaile auth` command group.
 *
 * Manages the CLI master-key strategy and secret provider health. Subcommands:
 * `status` (show current strategy and provider health), `set-key-strategy`
 * (switch between `keychain`, `passphrase`, and `machine`).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/auth#make-auth-command
 */
export function makeAuthCommand(): Command {
  const cmd = new Command("auth").description("Manage secret key strategy and provider status");

  cmd
    .command("status")
    .description("Show current key strategy and provider health")
    .action(async () => {
      const provider = new LocalSecretsProvider();
      const strategy = provider.getKeyStrategy();
      logInfo(`Key strategy: ${strategy}`);

      const health = await provider.testConnection();
      if (health.ok) {
        logOk(`Local provider: healthy (${health.latencyMs}ms)`);
      } else {
        logErr(`Local provider: ${health.error ?? "unhealthy"}`);
      }
    });

  cmd
    .command("set-key-strategy")
    .description("Set the master key strategy (keychain, passphrase, or machine)")
    .argument("<strategy>", `One of: ${VALID_STRATEGIES.join(", ")}`)
    .option(
      "--passphrase <value>",
      "Passphrase value (required for passphrase strategy; alternative to SKAILE_SECRETS_PASSPHRASE env var)",
    )
    .action(async (strategy: string, opts: { passphrase?: string }) => {
      if (!VALID_STRATEGIES.includes(strategy as KeyStrategy)) {
        logErr(`Invalid strategy "${strategy}". Must be one of: ${VALID_STRATEGIES.join(", ")}`);
        process.exit(1);
      }

      if (strategy === "passphrase" && !opts.passphrase && !process.env.SKAILE_SECRETS_PASSPHRASE) {
        logErr(
          "Passphrase required. Provide via --passphrase or set SKAILE_SECRETS_PASSPHRASE:\n" +
            "  export SKAILE_SECRETS_PASSPHRASE='...'\n" +
            "  skaile auth set-key-strategy passphrase",
        );
        process.exit(1);
      }

      const provider = new LocalSecretsProvider();
      try {
        await provider.setKeyStrategy(strategy as KeyStrategy, opts.passphrase);
        logOk(`Key strategy set to: ${strategy}`);
      } catch (e) {
        logErr(`Failed to set strategy: ${e instanceof Error ? e.message : String(e)}`);
        process.exit(1);
      }
    });

  return cmd;
}
